import { WarningOutline18Icon } from "@vygruppen/spor-icon-react";
import { format } from "date-fns";
import { GlobalEventModalContent } from "features/CenterContent/RoleContent/GlobalEvents/GlobalEventModalContent";
import { GlobalFormSchema } from "features/CenterContent/RoleContent/GlobalEvents/types/formSchema";
import { GlobalEventResponse } from "features/CenterContent/RoleContent/GlobalEvents/types/responseSchema";
import { EditGlobalEventCheckbox } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/EditGlobalEventCheckbox";
import { FC, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { ChevronDownButton } from "shared/components/buttons/ChevronButton";
import { PlusButton } from "shared/components/buttons/PlusButton";
import { DropsButton, Flex, HStack, Stack, Text } from "shared/components/Spor";
import { useTheme } from "styled-components";

type ButtonProps = {
  active: boolean;
  title: string;
  undoTitle: string;
  action: () => void;
  undoAction: () => void;
};

const DepublishReopenButton: FC<ButtonProps> = ({
  active,
  title,
  undoTitle,
  action,
  undoAction,
}) => (
  <Flex justifyContent="flex-end">
    <DropsButton
      onClick={() => (active ? undoAction() : action())}
      variant="tertiary"
      size="xs"
    >
      {active ? undoTitle : title}
    </DropsButton>
  </Flex>
);

type ReferencedGlobalEventProps = {
  globalEventFormMethods: UseFormReturn<GlobalFormSchema>;
  shouldPost: boolean;
  setShouldPost: (value: boolean) => void;
  shouldDepublish: boolean;
  setShouldDepublish: (value: boolean) => void;
  relatedGlobalInformation?: GlobalEventResponse;
};

export const ReferencedGlobalEvent: FC<ReferencedGlobalEventProps> = ({
  globalEventFormMethods,
  shouldPost,
  setShouldPost,
  shouldDepublish,
  setShouldDepublish,
  relatedGlobalInformation,
}) => {
  const [open, setopen] = useState(false);
  const [editChecked, setEditChecked] = useState(false);
  const theme = useTheme();
  const hasGlobalInfo = !!relatedGlobalInformation;

  useEffect(() => {
    if (!hasGlobalInfo) {
      setShouldPost(open);
    }
  }, [open]);

  useEffect(() => {
    if (hasGlobalInfo) {
      setShouldPost(editChecked);
    }
  }, [editChecked]);

  return (
    <Stack
      width="100%"
      gap="24px"
      paddingX="18px"
      paddingY="12px"
      boxShadow={`inset 0 0 0 1px ${theme.colorOutlineMain}`}
      borderRadius="sm"
    >
      <HStack justifyContent="space-between">
        <HStack>
          <WarningOutline18Icon />
          <Text variant="sm" fontWeight="bold">
            Global driftsmelding
          </Text>
        </HStack>
        {hasGlobalInfo ? (
          <ChevronDownButton active={open} setActive={setopen} />
        ) : (
          <PlusButton active={open} setActive={setopen} />
        )}
      </HStack>
      {open && (
        <>
          {hasGlobalInfo && (
            <Stack
              borderBottom={`1px solid ${theme.colorOutlineMain}`}
              gap="24px"
              paddingBottom="18px"
            >
              <Text variant="sm" width="100%">
                <span style={{ fontWeight: "bold" }}>Sist oppdatert:</span>{" "}
                {format(
                  relatedGlobalInformation.updatedAt.toDate(),
                  "dd.MM.yyyy 'kl.' HH:mm",
                )}
              </Text>
              {((relatedGlobalInformation.isVisible && !shouldDepublish) ||
                (!relatedGlobalInformation.isVisible && shouldPost)) && (
                <EditGlobalEventCheckbox
                  editGlobalEvent={editChecked}
                  setEditGlobalEvent={setEditChecked}
                />
              )}
              {shouldDepublish && (
                <Text variant="sm">
                  Global driftsmelding vil bli depublisert.
                </Text>
              )}
              {relatedGlobalInformation.isVisible ? (
                <DepublishReopenButton
                  active={shouldDepublish}
                  title="Depubliser driftsmelding"
                  undoTitle="Angre"
                  action={() => {
                    setShouldDepublish(true);
                    setEditChecked(false);
                  }}
                  undoAction={() => setShouldDepublish(false)}
                />
              ) : (
                <DepublishReopenButton
                  active={shouldPost}
                  title="Gjenåpne driftsmelding"
                  undoTitle="Angre gjenåpning"
                  action={() => setEditChecked(true)}
                  undoAction={() => setEditChecked(false)}
                />
              )}
            </Stack>
          )}
          <GlobalEventModalContent
            formMethods={globalEventFormMethods}
            disabled={hasGlobalInfo && !editChecked}
          />
        </>
      )}
    </Stack>
  );
};
